// Onboarding Service
// Manages user onboarding state and preferences

const ONBOARDING_STORAGE_KEY = 'ekko_onboarding_preferences';

interface OnboardingPreferences {
    hasCompletedGuide: boolean;
    dontShowAgain: boolean;
    lastCompletedStep: number;
    completedAt?: string;
    skippedAt?: string;
}

const DEFAULT_PREFERENCES: OnboardingPreferences = {
    hasCompletedGuide: false,
    dontShowAgain: false,
    lastCompletedStep: -1
};

// Get onboarding preferences from localStorage
export const getOnboardingPreferences = (): OnboardingPreferences => {
    try {
        const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return { ...DEFAULT_PREFERENCES, ...parsed };
        }
    } catch (error) {
        console.error('Error loading onboarding preferences:', error);
    }
    return DEFAULT_PREFERENCES;
};

// Save onboarding preferences to localStorage
export const saveOnboardingPreferences = (preferences: Partial<OnboardingPreferences>): void => {
    try {
        const current = getOnboardingPreferences();
        const updated = { ...current, ...preferences };
        localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
        console.error('Error saving onboarding preferences:', error);
    }
};

// Check if user should see the onboarding guide
export const shouldShowOnboarding = (): boolean => {
    const preferences = getOnboardingPreferences();
    return !preferences.hasCompletedGuide && !preferences.dontShowAgain;
};

// Mark onboarding as completed
export const markOnboardingCompleted = (): void => {
    saveOnboardingPreferences({
        hasCompletedGuide: true,
        completedAt: new Date().toISOString()
    });
};

// Mark onboarding as skipped (don't show again)
export const markOnboardingSkipped = (): void => {
    saveOnboardingPreferences({
        dontShowAgain: true,
        skippedAt: new Date().toISOString()
    });
};

// Reset onboarding preferences (for replay functionality)
export const resetOnboardingPreferences = (): void => {
    saveOnboardingPreferences({
        hasCompletedGuide: false,
        dontShowAgain: false,
        lastCompletedStep: -1,
        completedAt: undefined,
        skippedAt: undefined
    });
};

// Update current step progress
export const updateOnboardingProgress = (step: number): void => {
    saveOnboardingPreferences({
        lastCompletedStep: step
    });
};

// Check if this is a first-time user (can be used for additional welcome features)
export const isFirstTimeUser = (): boolean => {
    const preferences = getOnboardingPreferences();
    return preferences.lastCompletedStep === -1 && !preferences.hasCompletedGuide;
};

// Get onboarding statistics for debugging/analytics
export const getOnboardingStats = () => {
    const preferences = getOnboardingPreferences();
    return {
        preferences,
        shouldShow: shouldShowOnboarding(),
        isFirstTime: isFirstTimeUser()
    };
}; 